import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { PublicNav } from '../components/layout/PublicNav';
import { contentApi } from '../services/api';
import { detectLanguage, type OutputLanguage } from '../utils/detectLanguage';

interface PageSummary {
  slug: string;
  page_type: string;
  title: string;
  meta_description: string;
  created_at: string;
}

export function ContentLibrary() {
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    document.title = 'Indian Law Research Library | Urava';
    const desc = document.head.querySelector('meta[name="description"]');
    desc?.setAttribute('content',
      'Free guides on Indian law — bail, cheque bounce, property, family law and more — plus honest comparisons of legal research tools. By Urava.');
    contentApi.getPages().then(({ data }) => setPages(data)).finally(() => setIsLoading(false));
  }, []);

  const guides = pages.filter(p => p.page_type === 'learn');
  const comparisons = pages.filter(p => p.page_type === 'compare');

  // Group the guides by the language they're written in (detected from the
  // title's script, the same heuristic the rest of the app uses). English
  // first, then Hindi, then Malayalam — only render a group that has pages.
  const LANG_ORDER: OutputLanguage[] = ['en', 'hi', 'ml'];
  const GUIDE_SECTION_TITLE: Record<OutputLanguage, string> = {
    en: 'Legal Research Guides — English',
    hi: 'कानूनी गाइड — हिन्दी (Hindi)',
    ml: 'നിയമ ഗൈഡുകൾ — മലയാളം (Malayalam)',
  };
  const guidesByLang = LANG_ORDER
    .map(lang => ({ lang, items: guides.filter(p => detectLanguage(p.title) === lang) }))
    .filter(g => g.items.length > 0);

  const Section = ({ title, items, base }: { title: string; items: PageSummary[]; base: string }) => (
    items.length === 0 ? null : (
      <section className="mb-12">
        <h2 className="font-display text-xl font-medium text-legal-900 mb-4">{title}</h2>
        <div className="bg-white rounded-2xl border border-legal-200 overflow-hidden">
          {items.map(p => (
            <Link
              key={p.slug}
              to={`${base}/${p.slug}`}
              className="group flex items-start justify-between gap-4 px-5 py-4 border-b border-legal-100 last:border-b-0 hover:bg-accent-50/60 transition-colors"
            >
              <span>
                <span className="block font-display text-[15px] font-medium text-legal-900 group-hover:text-accent-700 transition-colors">
                  {p.title}
                </span>
                <span className="block text-xs text-legal-400 mt-1 line-clamp-2">{p.meta_description}</span>
              </span>
              <ArrowUpRight className="h-4 w-4 text-legal-300 group-hover:text-accent-600 flex-shrink-0 mt-1 transition-colors" />
            </Link>
          ))}
        </div>
      </section>
    )
  );

  return (
    <div className="min-h-screen bg-legal-50">
      <PublicNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <p className="text-[11px] uppercase tracking-[0.2em] text-accent-700 mb-3">Research Library</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium text-legal-900 leading-tight mb-3">
          Indian law, researched and written down.
        </h1>
        <p className="text-legal-500 mb-10 max-w-xl">
          Practical guides on questions advocates and litigants actually ask, and honest
          comparisons of legal research tools — published by Urava's research desk.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-20 text-legal-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : pages.length === 0 ? (
          <p className="text-legal-400 text-sm py-16 text-center">New articles are on the way — check back soon.</p>
        ) : (
          <>
            {guidesByLang.map(g => (
              <Section key={g.lang} title={GUIDE_SECTION_TITLE[g.lang]} items={g.items} base="/learn" />
            ))}
            <Section title="Tool Comparisons" items={comparisons} base="/compare" />
          </>
        )}
      </main>
    </div>
  );
}
